import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Lock, LogIn } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PinLockScreen({ onUnlock }) {
  const [digits, setDigits] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDigit = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);
    setError('');

    if (value && index < 3) {
      const next = document.getElementById(`lock-pin-${index + 1}`);
      if (next) next.focus();
    }

    if (value && index === 3) {
      const pin = [...newDigits.slice(0, 3), value].join('');
      if (pin.length === 4) {
        verifyPin(pin);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      const prev = document.getElementById(`lock-pin-${index - 1}`);
      if (prev) prev.focus();
    }
  };

  const verifyPin = async (pin) => {
    setIsVerifying(true);
    try {
      const res = await axios.post(`${API}/settings/verify-pin`, { pin });
      if (res.data.valid) {
        onUnlock();
      } else {
        setError('Incorrect PIN');
        setDigits(['', '', '', '']);
        setTimeout(() => {
          const first = document.getElementById('lock-pin-0');
          if (first) first.focus();
        }, 100);
      }
    } catch (err) {
      setError('Verification failed');
      setDigits(['', '', '', '']);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleForgotPin = () => {
    setShowConfirm(true);
  };

  const handleReEnterWithAccount = () => {
    localStorage.setItem('noyo_pin_reset', 'true');
    localStorage.removeItem('noyo_token');
    window.location.href = '/welcome';
  };

  useEffect(() => {
    const first = document.getElementById('lock-pin-0');
    // Don't auto-focus - let user tap in to see the lock screen first
    // if (first) first.focus();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-background z-[100] flex flex-col items-center justify-center"
      data-testid="pin-lock-screen"
    >
      <div className="text-center mb-10">
        <BookOpen className="w-12 h-12 text-primary mx-auto mb-4" />
        <h1 className="text-3xl font-serif font-medium text-foreground tracking-tight">NOYO</h1>
        <p className="text-xs text-muted-foreground tracking-[0.2em] mt-1">Know Yourself</p>
      </div>

      <div className="flex items-center space-x-2 mb-6">
        <Lock className="w-4 h-4 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Enter your PIN</p>
      </div>

      <div className="flex space-x-4 mb-6">
        {[0, 1, 2, 3].map(i => (
          <input
            key={i}
            id={`lock-pin-${i}`}
            type="password"
            inputMode="numeric"
            maxLength={1}
            value={digits[i]}
            onChange={(e) => handleDigit(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            disabled={isVerifying}
            className="w-14 h-16 text-center text-2xl font-bold bg-card border-2 border-border rounded-2xl focus:outline-none focus:border-primary transition-colors disabled:opacity-50"
            data-testid={`lock-pin-digit-${i}`}
          />
        ))}
      </div>

      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-red-500"
          data-testid="pin-lock-error"
        >
          {error}
        </motion.p>
      )}

      {isVerifying && (
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mt-2" />
      )}

      {!showConfirm ? (
        <button
          onClick={handleForgotPin}
          className="mt-8 text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="forgot-pin-button"
        >
          Forgot PIN?
        </button>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 text-center max-w-xs"
        >
          <p className="text-sm text-muted-foreground mb-4">
            Sign in again to verify your identity. Your PIN will be reset.
          </p>
          <button
            onClick={handleReEnterWithAccount}
            className="flex items-center justify-center space-x-2 mx-auto px-5 py-2.5 bg-primary text-primary-foreground rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
            data-testid="re-enter-account-button"
          >
            <LogIn className="w-4 h-4" />
            <span>Sign in to reset PIN</span>
          </button>
          <button
            onClick={() => setShowConfirm(false)}
            className="mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="cancel-forgot-pin"
          >
            Cancel
          </button>
        </motion.div>
      )}
    </motion.div>
  );
}

export default PinLockScreen;
